import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { ReserveHeader } from "@/components/ReserveHeader";
import { BackToDashboard } from "@/components/BackToDashboard";
import { Radio } from "lucide-react";

export const Route = createFileRoute("/_authenticated/bartender/radar")({
  component: RadarPage,
  head: () => ({
    meta: [
      { title: "Live shift radar · The Hospitality Reserve" },
      { name: "description", content: "See open bartender shifts near you on a live map — with distance and pay/hour at a glance." },
      { name: "robots", content: "noindex" },
    ],
  }),
  errorComponent: () => <div className="p-10 text-center">Could not load radar.</div>,
  notFoundComponent: () => <div className="p-10 text-center">Not found.</div>,
});

type MapShift = {
  id: string;
  role_type: string;
  hourly_rate: number;
  start_time: string;
  latitude: number | null;
  longitude: number | null;
  postcode: string | null;
};

function escapeHtml(v: string): string {
  return v
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function RadarPage() {
  const mapRef = useRef<HTMLDivElement | null>(null);
  const mapObj = useRef<{ remove: () => void } | null>(null);
  const [shifts, setShifts] = useState<MapShift[] | null>(null);
  const [me, setMe] = useState<{ lat: number; lng: number } | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // Load bartender coords + open shifts
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: userData } = await supabase.auth.getUser();
      const uid = userData.user?.id;
      if (uid) {
        const { data: profile } = await supabase
          .from("profiles").select("latitude, longitude").eq("id", uid).maybeSingle();
        if (!cancelled && profile?.latitude != null && profile?.longitude != null) {
          setMe({ lat: Number(profile.latitude), lng: Number(profile.longitude) });
        }
      }
      const { data, error } = await (supabase.rpc as unknown as (
        n: string,
      ) => Promise<{ data: MapShift[] | null; error: unknown }>)("browse_open_shifts");
      if (cancelled) return;
      if (error) { setErr("Could not load shifts"); return; }
      setShifts(
        (data ?? [])
          .filter((s) => s.latitude != null && s.longitude != null)
          .slice(0, 200),
      );
    })();
    return () => { cancelled = true; };
  }, []);

  // Render the Leaflet / OpenStreetMap map once shifts + container are ready.
  useEffect(() => {
    if (!mapRef.current || !shifts) return;
    let cancelled = false;

    (async () => {
      const [L] = await Promise.all([
        import("leaflet").then((m) => m.default ?? m),
        import("leaflet/dist/leaflet.css"),
      ]);
      if (cancelled || !mapRef.current) return;

      mapObj.current?.remove();

      const center: [number, number] = me
        ? [me.lat, me.lng]
        : shifts[0]
          ? [shifts[0].latitude!, shifts[0].longitude!]
          : [51.5074, -0.1278];

      const map = L.map(mapRef.current, {
        center,
        zoom: 10,
        zoomControl: true,
        attributionControl: true,
      });
      mapObj.current = map as unknown as { remove: () => void };

      L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
        maxZoom: 19,
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      }).addTo(map);

      if (me) {
        L.circleMarker([me.lat, me.lng], {
          radius: 8,
          color: "#ffffff",
          weight: 2,
          fillColor: "#2E6B8A",
          fillOpacity: 1,
        })
          .addTo(map)
          .bindTooltip("You are here");
      }

      shifts.forEach((s) => {
        const rate = (s.hourly_rate / 100).toFixed(0);
        const icon = L.divIcon({
          className: "hr-radar-pin",
          html: `<span>£${escapeHtml(rate)}</span>`,
          iconSize: [40, 40],
          iconAnchor: [20, 20],
        });
        const when = new Date(s.start_time).toLocaleString("en-GB", {
          weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
        });
        L.marker([s.latitude!, s.longitude!], { icon, title: s.role_type })
          .addTo(map)
          .bindPopup(
            `<div style="color:#0e2433;font-family:system-ui;min-width:180px">
               <div style="font-weight:700;text-transform:capitalize">${escapeHtml(s.role_type)}</div>
               <div style="font-size:12px;color:#555">${escapeHtml(when)}</div>
               <div style="font-size:12px;color:#555">${escapeHtml(s.postcode ?? "")}</div>
               <div style="margin-top:6px;font-weight:600">£${escapeHtml((s.hourly_rate / 100).toFixed(2))}/hr</div>
             </div>`,
          );
      });

      // Fit to markers when we have several.
      const pts: Array<[number, number]> = shifts.map((s) => [s.latitude!, s.longitude!]);
      if (me) pts.push([me.lat, me.lng]);
      if (pts.length > 1) map.fitBounds(L.latLngBounds(pts).pad(0.2), { maxZoom: 13 });

      setTimeout(() => map.invalidateSize(), 50);
    })().catch(() => setErr("Map failed to load"));

    return () => {
      cancelled = true;
      mapObj.current?.remove();
      mapObj.current = null;
    };
  }, [shifts, me]);

  return (
    <div className="min-h-screen bg-background">
      <ReserveHeader />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-4">
        <BackToDashboard />
        <header className="space-y-2">
          <p className="text-[0.6rem] tracking-wide-luxury uppercase text-muted-foreground flex items-center gap-2">
            <Radio className="w-3 h-3 text-accent" /> Live radar
          </p>
          <h1 className="font-display text-3xl">Open shifts near you</h1>
          <p className="text-sm text-muted-foreground">
            Gold markers show open shifts with the hourly rate. Tap for details.
            {shifts && ` ${shifts.length} shown.`}
          </p>
        </header>
        {err && <p className="text-sm text-destructive">{err}</p>}
        <div
          ref={mapRef}
          className="w-full h-[70vh] rounded-2xl border border-border bg-card overflow-hidden z-0"
          aria-label="Map of open shifts"
        />
      </main>
    </div>
  );
}
